import { ref, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/composables/useApi';

// All available pages in the sidebar
export const ALL_PAGES = {
  dashboard: 'Dashboard',
  jobs: 'Jobs',
  projects: 'Projects',
  boq: 'BOQ',
  catalogue: 'Catalogue',
  orders: 'Orders',
  rfqs: 'RFQs',
  rfis: 'RFIs',
  inspections: 'Inspections',
  defects: 'Defects',
  safety: 'Safety',
  'daily-logs': 'Daily Logs',
  timesheets: 'Timesheets',
  suppliers: 'Suppliers',
  contacts: 'Contacts',
  accounting: 'Accounting',
  documents: 'Documents',
  transmittals: 'Transmittals',
  reports: 'Reports',
  'ai-activity': 'AI Activity',
  'recycle-bin': 'Recycle Bin',
  settings: 'Settings',
} as const;

// Accounting tabs
export const ACCOUNTING_TABS = {
  dashboard: 'Dashboard',
  invoices: 'Invoices',
  entry: 'New Invoice',
  matching: 'PO Matching',
  payments: 'Payments',
  audit: 'Audit Trail',
  progress: 'Progress Claim',
  'budget-progress': 'Budget %',
  claims: 'Claims',
  receipts: 'Receipts',
  settings: 'Settings',
} as const;

export type PageKey = keyof typeof ALL_PAGES;
export type AccountingTabKey = keyof typeof ACCOUNTING_TABS;

export interface FeatureAccessConfig {
  pages: Partial<Record<PageKey, boolean>>;
  tabs?: {
    accounting?: Partial<Record<AccountingTabKey, boolean>>;
  };
}

export interface RoleFeatureAccess {
  [role: string]: FeatureAccessConfig;
}

// Singleton state — loaded once and shared across all components
const roleDefaults = ref<RoleFeatureAccess | null>(null);
const userOverrides = ref<FeatureAccessConfig | null>(null);
const loaded = ref(false);
const loading = ref(false);

// Default config when nothing is configured: all access
function getFullAccessConfig(): FeatureAccessConfig {
  const pages: Record<string, boolean> = {};
  for (const key of Object.keys(ALL_PAGES)) {
    pages[key] = true;
  }
  const accountingTabs: Record<string, boolean> = {};
  for (const key of Object.keys(ACCOUNTING_TABS)) {
    accountingTabs[key] = true;
  }
  return { pages: pages as FeatureAccessConfig['pages'], tabs: { accounting: accountingTabs as FeatureAccessConfig['tabs'] extends undefined ? never : NonNullable<FeatureAccessConfig['tabs']>['accounting'] } };
}

export function useFeatureAccess() {
  const authStore = useAuthStore();

  async function loadFeatureAccess() {
    if (loaded.value || loading.value) return;
    loading.value = true;

    try {
      // Load role defaults from company_settings.metadata.featureAccess
      const settings = await api.settings.getCompanySettings();
      const metadata = (settings?.metadata || {}) as Record<string, unknown>;
      roleDefaults.value = (metadata.featureAccess as RoleFeatureAccess) || null;

      // Load user overrides from user_config.metadata.featureAccess
      const userId = authStore.user?.id;
      if (userId) {
        try {
          const userConfig = await api.settings.getUser(userId);
          const userMeta = (userConfig?.metadata || {}) as Record<string, unknown>;
          userOverrides.value = (userMeta.featureAccess as FeatureAccessConfig) || null;
        } catch {
          // User config may not exist yet
          userOverrides.value = null;
        }
      }
    } catch {
      // If settings fail to load, default to full access
      roleDefaults.value = null;
      userOverrides.value = null;
    } finally {
      loaded.value = true;
      loading.value = false;
    }
  }

  // Resolve effective access for current user
  function getEffectiveConfig(): FeatureAccessConfig {
    const role = authStore.tenantRole;

    // Admin always gets full access
    if (role === 'admin') return getFullAccessConfig();

    // No config? Full access (backwards compatible)
    if (!roleDefaults.value) return getFullAccessConfig();

    const roleConfig = roleDefaults.value[role || 'user'] || getFullAccessConfig();

    // Merge user overrides on top of role defaults
    if (!userOverrides.value) return roleConfig;

    const merged: FeatureAccessConfig = {
      pages: { ...roleConfig.pages, ...userOverrides.value.pages },
      tabs: {
        accounting: {
          ...(roleConfig.tabs?.accounting || {}),
          ...(userOverrides.value.tabs?.accounting || {}),
        },
      },
    };

    return merged;
  }

  function canAccessPage(pageKey: PageKey): boolean {
    if (!loaded.value) return true; // Not loaded yet, allow access
    const config = getEffectiveConfig();
    return config.pages[pageKey] !== false; // Default to true if not set
  }

  function canAccessTab(pageKey: string, tabKey: string): boolean {
    if (!loaded.value) return true;
    const config = getEffectiveConfig();
    if (pageKey === 'accounting' && config.tabs?.accounting) {
      return config.tabs.accounting[tabKey as AccountingTabKey] !== false;
    }
    return true; // Default to true
  }

  const effectiveConfig = computed(() => getEffectiveConfig());

  // Reset state (for use when switching tenants)
  function reset() {
    loaded.value = false;
    loading.value = false;
    roleDefaults.value = null;
    userOverrides.value = null;
  }

  return {
    loaded,
    loading,
    roleDefaults,
    userOverrides,
    effectiveConfig,
    loadFeatureAccess,
    canAccessPage,
    canAccessTab,
    getFullAccessConfig,
    reset,
  };
}
